/**
 * api.js — MedLandmark frontend API client
 *
 * Uses RunPod's async pattern:
 *   1. POST /run         → returns { id: "job-id" } immediately
 *   2. Poll GET /status/{id}  every POLL_INTERVAL_MS until COMPLETED or FAILED
 *
 * While IN_PROGRESS the status response carries a progress payload in `output`:
 *   { stage: "segmenting", current: 14, total: 63, landmark: "LIST_A01_l" }
 *
 * The onProgress callback is called on every status poll that returns progress
 * data, so the UI can update in real time.
 */

const RUNPOD_ENDPOINT_ID = "v7vdvlpsv6bwf6";
const RUNPOD_BASE_URL    = `https://api.runpod.ai/v2/${RUNPOD_ENDPOINT_ID}`;

// API key is injected at build time via .env:
//   REACT_APP_RUNPOD_API_KEY=rpa_...
const API_KEY = process.env.REACT_APP_RUNPOD_API_KEY;

const POLL_INTERVAL_MS = 2000;   // poll every 2 seconds
const MAX_WAIT_MS      = 900000; // 15 minutes — give up after this

// ── Helpers ───────────────────────────────────────────────────────────────────

function runpodHeaders() {
  return {
    "Content-Type":  "application/json",
    "Authorization": `Bearer ${API_KEY}`,
  };
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Convert a File object to a base64 string (without the data-URL prefix).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// ── Core API calls ────────────────────────────────────────────────────────────

/**
 * Submit an inference job to RunPod.
 * @returns {Promise<string>} job ID
 */
async function submitJob(file) {
  const image_base64 = await fileToBase64(file);

  const response = await fetch(`${RUNPOD_BASE_URL}/run`, {
    method:  "POST",
    headers: runpodHeaders(),
    body: JSON.stringify({
      input: {
        image_base64,
        filename: file.name,
      },
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to submit job: ${response.status} — ${text}`);
  }

  const data = await response.json();
  if (!data.id) throw new Error("RunPod did not return a job ID");
  return data.id;
}

/**
 * Get the current status of a RunPod job.
 * @returns {Promise<{status: string, output: any}>}
 */
async function getJobStatus(jobId) {
  const response = await fetch(`${RUNPOD_BASE_URL}/status/${jobId}`, {
    headers: runpodHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Status check failed: ${response.status}`);
  }

  return response.json();
  // Shape: { id, status, output, error? }
  // status values: IN_QUEUE, IN_PROGRESS, COMPLETED, FAILED, CANCELLED, TIMED_OUT
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Run the full inference pipeline on an X-ray image.
 *
 * @param {File}     file        - The image file to analyse
 * @param {Function} onProgress  - Called with progress data while running:
 *                                   onProgress({ stage, current, total, landmark })
 *                                 stage values: "loading_models" | "yolo_detection" | "segmenting"
 *
 * @returns {Promise<Object>} Result object:
 *   {
 *     landmarks:              { "LIST_A01_l": [x, y], ... },
 *     annotated_image_base64: "<base64 PNG>",
 *     statistics:             { total_detected: 63 },
 *     metadata:               { filename, inference_time_seconds, timestamp }
 *   }
 */
export async function runInference(file, onProgress = () => {}) {
  if (!API_KEY) {
    throw new Error(
      "REACT_APP_RUNPOD_API_KEY is not set. " +
      "Add it to frontend/.env and rebuild."
    );
  }

  // 1. Submit job
  onProgress({ stage: "submitting" });
  const jobId = await submitJob(file);
  console.log(`Job submitted: ${jobId}`);

  // 2. Poll until complete
  const deadline = Date.now() + MAX_WAIT_MS;

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);

    const statusData = await getJobStatus(jobId);
    const { status, output, error } = statusData;

    console.log(`Job ${jobId}: ${status}`, output ?? "");

    switch (status) {
      case "IN_QUEUE":
        onProgress({ stage: "in_queue" });
        break;

      case "IN_PROGRESS":
        // output contains the latest progress_update payload from handler.py
        if (output && output.stage) {
          onProgress(output);
        }
        break;

      case "COMPLETED":
        if (output && output.error) {
          throw new Error(`Inference failed: ${output.error}`);
        }
        return output;   // ← final result

      case "FAILED":
      case "CANCELLED":
      case "TIMED_OUT":
        throw new Error(
          `Job ${status.toLowerCase()}: ${error || "no details available"}`
        );

      default:
        console.warn(`Unknown status: ${status}`);
    }
  }

  throw new Error("Timed out waiting for inference result (10 minutes)");
}
